import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, Plus, Upload, Eye } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { themeStore } from '../../../stores/theme';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@tanstack/react-store';
import { useListLocationsQuery, useAddNewLocation, useChangeLocationStatus, useUpdateLocation, useDeleteLocation } from '../../../hooks/useLocations';
import { useSubscriptionPackagesListQuery } from '../../../hooks/useSubscriptions';
import { useUser } from '../../../hooks/useAuth';
import { noData } from '../../ui/media';
import { useSingleSubmit } from '../../../hooks/useSingleSubmit';
import locationIcon from "../../../assets/icons/calendar.png"
import toast from 'react-hot-toast';
import AddressAutocomplete from '../../common/AddressAutoComplete';
import Modal from '../../common/ModalComponent';
import SingleSubmitForm from '../../common/SingleSubmitForm';
import 'react-datepicker/dist/react-datepicker.css';

const LocationsPage = () => {
    const { isDarkMode } = useStore(themeStore)
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [locationImage, setLocationImage] = useState(null);
    const [locationsList, setLocationsList] = useState(null);
    const [name, setName] = useState("");
    const [productId, setProductId] = useState("");
    const [location, setLocation] = useState({
        coordinates: [],
        type: "point",
        name: ""
    });
    const [userData, setUserData] = useState("");
    const [subscriptionPackagesList, setSubscriptionPackagesList] = useState("");
    const { data: locationsResponse, isSuccess } = useListLocationsQuery()
    const { data: userDataResponse, isSuccess: userIsSuccess } = useUser()
    const { canSubmit, resetSubmit } = useSingleSubmit();
    const changeStatusMutation = useChangeLocationStatus();
    const addNewLocationMutation = useAddNewLocation()
    const updateLocationMutation = useUpdateLocation()
    const deleteLocationMutation = useDeleteLocation()
    const queryClient = useQueryClient();
    const navigate = useNavigate()
    const { data: subscriptionPackagesResponse, isSuccess: subscriptionPackagesSuccess } = useSubscriptionPackagesListQuery(
        userIsSuccess && userDataResponse?.data?.data?.user_type
            ? `?product_type=${userDataResponse?.data?.data?.user_type == 2 ? "event" : "security"}`
            : null,
        {
            enabled: !!(userIsSuccess && userDataResponse?.data?.data?.user_type),
        }
    );
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    // upgrade Plan
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [priceDifference, setPriceDifference] = useState(0);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [currentPlan, setCurrentPlan] = useState(null);

    const colors = {
        background: isDarkMode ? '#121212' : '#ffffff',
        card: isDarkMode ? '#1e1e1e' : '#f5f7fb',
        text: isDarkMode ? '#e0e0e0' : '#333333',
        secondaryText: isDarkMode ? '#a0a0a0' : '#666666',
        border: isDarkMode ? '#333333' : '#e0e0e0',
        primary: '#2563eb',
        accent: isDarkMode ? '#3b82f6' : '#2563eb',
        footer: isDarkMode ? '#1a1a1a' : '#f0f4f8',
        footerText: isDarkMode ? '#a0a0a0' : '#555555',
        modalBackground: isDarkMode ? '#1a1a1a' : '#ffffff',
        modalHeader: isDarkMode ? '#1e293b' : '#e5edff',
        inputBackground: isDarkMode ? '#2d2d2d' : '#ffffff'
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLocationImage(file); // store file
            const imageURL = URL.createObjectURL(file);
            setImagePreview(imageURL);
        }
    };

    const resetStates = () => {
        setName('');
        setProductId('');
        setLocation('');
        setLocationImage(null);
        setImagePreview('');
    }

    // Handle add location
    const handleAddLocation = () => {
        if (!canSubmit()) return;
        if (name == "") {
            toast.error("Location Name is not allowed to empty")
            resetSubmit();
            return
        }
        if (productId == "") {
            toast.error("Product Id is not allowed to empty")
            resetSubmit();
            return
        }
        if (location?.name == "") {
            toast.error("Location is not allowed to empty")
            resetSubmit();
            return
        }
        const locationFormData = new FormData();
        locationFormData.append('company_id', userData?._id)
        locationFormData.append("name", name);
        locationFormData.append("product_id", productId);
        locationFormData.append("location", JSON.stringify(location));
        if (locationImage) {
            locationFormData.append('location_media', locationImage);
        }
        addNewLocationMutation.mutate(locationFormData, {
            onSuccess: (data) => {
                resetSubmit();
                setShowAddModal(false);
                resetStates()
                toast.success("New Location Added Successfully")
                queryClient.invalidateQueries({ queryKey: ['locations'] });
                navigate(`/make-payment/${data?.data?.data?._id}/${userData?._id}/internal/location/purchase/${productId}`)
            },
            onError: (err) => {
                resetSubmit();
                console.log("error", err)
                const message = err.response?.data?.message || "Add New Location Action Failed"
                toast.error(message)
            }
        })
    };

    // Toggle status
    const toggleStatus = (location_id, currentStatus) => {
        changeStatusMutation.mutate({ status: currentStatus === 1 ? 0 : 1, location_id },
            {
                onSuccess: () => {
                    toast.success("Location status updated successfully");
                    queryClient.invalidateQueries(['locations']); // Adjust key if needed
                },
                onError: (err) => {
                    const message = err.response?.data?.message || "Failed to update location status";
                    toast.error(message);
                },
            });
    };

    const handleUpgrade = (location) => {
        setSelectedLocation(location)
        setCurrentPlan(subscriptionPackagesList?.find((packageObj) => packageObj?.id == location?.product_id ?? null))
        setShowUpgradeModal(true)
    }

    // Open edit modal
    const handleEditClick = (location) => {
        setCurrentLocation(location);
        setImagePreview(location?.media ? `${process.env.REACT_APP_IMAGE_URL}/${location?.media}` : "")
        setName(location?.name)
        setLocation(location?.location)
        setShowEditModal(true);
    };

    // Open delete modal
    const handleDeleteClick = (location) => {
        setCurrentLocation(location);
        setShowDeleteModal(true);
    };

    // Handle update location
    const handleUpdateLocation = () => {
        if (name == "") {
            toast.error("Location Name is not allowed to empty")
            return
        }
        if (location?.name == "") {
            toast.error("Location is not allowed to empty")
            return
        }
        const locationFormData = new FormData();
        locationFormData.append('location_id', currentLocation?._id)
        locationFormData.append("name", name);
        locationFormData.append("location", JSON.stringify(location));
        if (locationImage) {
            locationFormData.append('location_media', locationImage);
        }
        updateLocationMutation.mutate(locationFormData, {
            onSuccess: () => {
                toast.success("Location Updated Successfully")
                queryClient.invalidateQueries({ queryKey: ['locations'] });
                queryClient.invalidateQueries({ queryKey: ['subscriptionPackagesList'] });
                setShowAddModal(false);
                resetStates()
            },
            onError: (err) => {
                const message = err.response?.data?.message || "Update Location Action Failed"
                toast.error(message)
            }
        })
        setShowEditModal(false);
    };

    // Handle delete location
    const handleDeleteLocation = () => {
        deleteLocationMutation.mutate({ location_id: currentLocation._id }, {
            onSuccess: () => {
                toast.success("Location deleted successfully");
                queryClient.invalidateQueries(['locations']); // Adjust key if needed
            },
            onError: (err) => {
                const message = err.response?.data?.message || "Failed to delete location";
                toast.error(message);
            },
        });
        setShowDeleteModal(false);
    };

    useEffect(() => {
        if (isSuccess) {
            setLocationsList(locationsResponse?.data?.data?.result)
        }
    }, [isSuccess, locationsResponse])

    useEffect(() => {
        if (subscriptionPackagesSuccess) {
            setSubscriptionPackagesList(subscriptionPackagesResponse?.data?.data)
        }
    }, [subscriptionPackagesSuccess, subscriptionPackagesResponse])

    useEffect(() => {
        setUserData(userDataResponse?.data?.data)
    }, userDataResponse)

    useEffect(() => {
        const plan = subscriptionPackagesList && subscriptionPackagesList.find(p => p.id === selectedPlanId);
        setSelectedPlan(plan);
        if (plan) {
            const diff = plan?.price_value - currentPlan?.price_value;
            setPriceDifference(diff > 0 ? diff : 0); // Only pay extra, no refunds
        } else {
            setPriceDifference(0);
        }
    }, [selectedPlanId, currentPlan, subscriptionPackagesList]);

    return (
        <div className={`w-full min-h-screen flex flex-col ${isDarkMode ? "bg-transparent" : "bg-white"}`} >
            {/* Main Content */}
            <main className="flex-grow py-8 px-6">
                <div className="container mx-auto">
                    {/* Locations Header */}
                    <div className="flex justify-between items-center mb-8">
                        <h1 className="text-3xl font-bold text-center w-full" style={{ color: colors.text }}>LOCATIONS</h1>
                        <button
                            onClick={() => { queryClient.invalidateQueries({ queryKey: ['subscriptionPackagesList'] }); setShowAddModal(true) }}
                            className={` justify-center py-2 w-[200px] rounded-full border text-center ${isDarkMode ? "border-white text-white" : "border-blue-600 text-blue-600"} flex items-center`}>
                            <Plus size={18} className="mr-1" />
                            Add Location
                        </button>
                    </div>
                    {/* Locations Table */}
                    <div className="overflow-x-auto bg-transparent">
                        <table className="min-w-full table-auto border-separate border-spacing-y-3">
                            <thead>
                                <tr className={`rounded-lg ${isDarkMode ? "bg-[rgba(20,35,58,0.8)]" : "bg-blue-100"}`}>
                                    <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Image</th>
                                    <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Name</th>
                                    <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Access Code</th>
                                    <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Location</th>
                                    <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>No. of Cameras</th>
                                    <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Payment Status</th>
                                    <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Status</th>
                                    <th className={`px-4 py-4 text-left font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}>Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {locationsList?.length > 0 ?
                                    locationsList?.map((location) => (
                                        <tr key={location.id} className={`rounded-lg ${isDarkMode ? "bg-[rgba(20,35,58,0.8)]" : "bg-blue-100"}`}>
                                            <td className="px-4 py-4">
                                                <img src={location?.media ? `${process.env.REACT_APP_IMAGE_URL}/${location?.media}` : locationIcon} alt={location?.name} className="h-10" />
                                            </td>
                                            <td className="px-4 py-4 font-medium" style={{ color: colors.text }}>{location?.name}</td>
                                            <td className="px-4 py-4" style={{ color: colors.secondaryText }}>{
                                                location?.payment_status == 2 ? location?.access_code : "******"
                                            }</td>
                                            <td className="px-4 py-4" style={{ color: colors.text }}>{location?.location?.name}</td>
                                            <td className="px-4 py-4 text-left" style={{ color: colors.text }}>{location?.camera_limit}</td>
                                            <td className="px-4 py-4 text-left" style={{ color: colors.text }}>{
                                                location?.payment_status == 1 ? "Pending" : location?.payment_status == 2 ? "Paid" : "Rejected"
                                            }</td>
                                            <td className="px-4 py-4">
                                                <button
                                                    disabled={changeStatusMutation?.isLoading}
                                                    onClick={() => toggleStatus(location?._id, location?.status)}
                                                    className="focus:outline-none">
                                                    <div className={`w-12 h-6 rounded-full flex items-center ${location?.status ? 'bg-green-500' : 'bg-gray-300'} p-1 transition-colors duration-200`}>
                                                        <div className={`w-4 h-4 rounded-full bg-white transform transition-transform duration-200 ${location.status ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                    </div>
                                                </button>
                                            </td>
                                            <td className="px-4 py-7 flex space-x-4">
                                                {location?.payment_status == 1 ?
                                                    <button
                                                        onClick={() => navigate(`/make-payment/${location?._id}/${userData?._id}/internal/location/purchase/${location?.product_id}`)}
                                                        className=" h-8 border rounded-full bg-blue-900 text-white w-[100px]">
                                                        Pay Now
                                                    </button>
                                                    :
                                                    <button
                                                        onClick={() => handleUpgrade(location)}
                                                        className=" h-8 border rounded-full bg-blue-900 text-white w-[100px]">
                                                        Upgrade
                                                    </button>
                                                }
                                                <button
                                                    onClick={() => navigate(`/locations/details/${location?._id}`)}
                                                    className="text-blue-600 hover:text-blue-800">
                                                    <Eye size={20} />
                                                </button>
                                                <button
                                                    onClick={() => handleEditClick(location)}
                                                    className="text-blue-600 hover:text-blue-800">
                                                    <Pencil size={20} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteClick(location)}
                                                    className="text-red-600 hover:text-red-800">
                                                    <Trash2 size={20} />
                                                </button>
                                            </td>
                                        </tr>
                                    )) :
                                    <tr>
                                        <td colSpan={9}>
                                            <div className={` mx-auto w-[50%] py-16 ${isDarkMode ? "bg-[rgba(0,0,0,0.5)]" : "bg-[rgba(255,255,255,1)]"}`}>
                                                <div className="flex justify-center items-center pt-8 mb-8">
                                                    <img
                                                        className='w-[250px]'
                                                        src={noData} />
                                                </div>
                                                <p className={`text-2xl ${isDarkMode ? "text-white" : "text-gray-800"} text-center`}>NO LOCATIONS YET</p>
                                                <div className='w-full flex items-center justify-center py-3'>
                                                    <button
                                                        onClick={() => setShowAddModal(true)}
                                                        className={` justify-center py-2 w-[200px] rounded-full border text-center ${isDarkMode ? "border-white text-white" : "border-blue-600 text-blue-600"} flex items-center mt-8`}>
                                                        <Plus size={18} className="mr-1" />
                                                        Add New Location
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
            {/* Add Location Modal */}
            <Modal isDarkMode={isDarkMode} key={"addform"} title="ADD NEW LOCATION" isOpen={showAddModal} onClose={() => setShowAddModal(false)}>
                <SingleSubmitForm onSubmit={handleAddLocation}>
                    <p className={`text-sm text-center mb-1 ${isDarkMode ? "text-white" : "text-gray-800"}`}>Upload image</p>
                    <div className="mb-6 flex justify-center">
                        <div className="w-20 h-20 border-2 border-dashed border-blue-500 rounded-lg flex flex-col items-center justify-center cursor-pointer">
                            {imagePreview ? (
                                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" />
                            ) : (
                                <Upload size={24} className="text-blue-600" />
                            )}
                            <input
                                type='file'
                                style={{ position: 'absolute', height: "90px", width: '90px', opacity: 0, cursor: 'pointer' }}
                                accept='image/*'
                                onChange={handleImageChange}
                            />
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Name</label>
                        <input
                            type="text"
                            value={name}
                            placeholder="Name"
                            className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onChange={(e) => setName(e.target.value)}
                            style={{
                                backgroundColor: colors.inputBackground,
                                borderColor: colors.border,
                                color: colors.text
                            }}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Select Plan</label>
                        <select
                            value={productId}
                            className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onChange={(e) => setProductId(e.target.value)}
                            style={{
                                backgroundColor: colors.inputBackground,
                                borderColor: colors.border,
                                color: colors.text
                            }}>
                            <option selected={true} value={""}>Select Plan</option>
                            {subscriptionPackagesList && subscriptionPackagesList?.map((packageObj) => (
                                <option value={packageObj?.id}>{`${packageObj?.description} | ($${packageObj?.price_value})`}</option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Location</label>
                        <AddressAutocomplete
                            placeholder={"Enter Location"}
                            value={location}
                            onSelect={(val) => setLocation(val)}
                        />
                    </div>
                    <div className='text-center'>
                        <button
                            type='submit'
                            disabled={addNewLocationMutation?.isLoading ? true : false}
                            className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mt-4">
                            {addNewLocationMutation?.isLoading ? "Processing..." : "Pay Now"}
                        </button>
                    </div>
                </SingleSubmitForm>
            </Modal>
            {/* Edit Location Modal */}
            <Modal isDarkMode={isDarkMode} key={"editform"} title="EDIT LOCATION" isOpen={showEditModal} onClose={() => setShowEditModal(false)}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        handleUpdateLocation()
                    }}>
                    <p className={`text-sm text-center mb-1 ${isDarkMode ? "text-white" : "text-gray-800"}`}>Upload image</p>
                    <div className="mb-6 flex justify-center">
                        <div className="w-20 h-20 border-2 border-dashed border-blue-500 rounded-lg flex flex-col items-center justify-center cursor-pointer">
                            {imagePreview ? (
                                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" />
                            ) : (
                                <Upload size={24} className="text-blue-600" />
                            )}
                            <input
                                type='file'
                                style={{ position: 'absolute', height: "90px", width: '90px', opacity: 0, cursor: 'pointer' }}
                                accept='image/*'
                                onChange={handleImageChange}
                            />
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Name</label>
                        <input
                            type="text"
                            value={name}
                            placeholder="Name"
                            className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onChange={(e) => setName(e.target.value)}
                            style={{
                                backgroundColor: colors.inputBackground,
                                borderColor: colors.border,
                                color: colors.text
                            }}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>Location</label>
                        <AddressAutocomplete
                            placeholder={"Enter Location"}
                            value={location}
                            onSelect={(val) => setLocation(val)}
                        />
                    </div>
                    <div className='text-center'>
                        <button
                            className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mt-4">
                            Update
                        </button>
                    </div>
                </form>
            </Modal>
            {/* Delete Location Modal */}
            <Modal isDarkMode={isDarkMode} key={"deleteform"} title="DELETE LOCATION" isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
                <div className="text-center mb-6">
                    <p className="mb-6" style={{ color: colors.text }}>Are you sure you want to delete this location?</p>
                    <div className='text-center mt-5'>
                        <button
                            onClick={() => setShowDeleteModal(false)}
                            className="w-[200px] py-3 text-center font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition rounded-full"
                            style={{ color: colors.text }}>
                            Cancel
                        </button>
                        <button
                            onClick={handleDeleteLocation}
                            className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mb-3">
                            Delete
                        </button>
                    </div>
                </div>
            </Modal>
            {/* Upgrade Location Modal */}
            <Modal isDarkMode={isDarkMode} key={"upgradeform"} title="UPGRADE LOCATION" isOpen={showUpgradeModal} onClose={() => { setSelectedPlan(null); setSelectedPlanId(''); setShowUpgradeModal(false) }}>
                <div className="space-y-3">
                    <div className={`${isDarkMode?"bg-gray-900 text-white": "bg-white text-gray-900"} p-4 rounded-xl shadow-sm`}>
                        <h2 className="text-lg font-semibold">Current Plan</h2>
                        <p>You paid: <strong>${currentPlan?.price_value}</strong></p>
                        <p>Plan Name: {currentPlan?.description}</p>
                    </div>
                    <div className={`${isDarkMode?"bg-gray-900 text-white": "bg-white text-gray-900"} p-4 rounded-xl shadow-sm border`}>
                        <label className="block mb-2 font-medium">
                            Select New Plan:
                        </label>
                        {subscriptionPackagesList && subscriptionPackagesList.filter((spl) => spl?.price_value > currentPlan?.price_value).length == 0 ?
                            <>
                                <label className="block mb-2 font-medium text-red-700">
                                    Sorry !!! You have already Subscribed a higher value package
                                </label>
                            </>
                            :
                            <select
                                value={selectedPlanId}
                                onChange={e => setSelectedPlanId(e.target.value)}
                                className={`${isDarkMode?"bg-gray-900 text-white": "bg-white text-gray-900"} w-full p-2 border rounded`}>
                                <option value="">-- Choose a Upgraded Plan --</option>
                                {subscriptionPackagesList && subscriptionPackagesList.filter((spl) => spl?.price_value > currentPlan?.price_value).map((plan) => (
                                    <option key={plan.id} value={plan?.id}>
                                        {plan?.description} - ${plan?.price_value}
                                    </option>
                                ))}
                            </select>
                        }
                    </div>
                    {selectedPlan && (
                        <>
                            <div className={`${isDarkMode?"bg-gray-900 text-white": "bg-white text-gray-900"} p-4 rounded-xl border border-blue-300`}>
                                <h3 className="font-semibold mb-1">Upgrade Summary:</h3>
                                <p>New Plan Worth: <strong>${selectedPlan?.price_value}</strong></p>
                                <p>Current Plan Value: <strong>${currentPlan?.price_value}</strong></p>
                                <p className="mt-2 text-blue-600 font-medium">
                                    After adjustment, you have to pay: <strong>${priceDifference?.toFixed(2)}</strong>
                                </p>
                            </div>
                            <div className='text-right'>
                                <button
                                    onClick={() => navigate(`/make-payment/${selectedLocation?._id}/${userData?._id}/internal/location/upgrade/${selectedPlan?.id}`)}
                                    className="w-[200px] py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition mt-2">
                                    Make Payment
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default LocationsPage;